"use client";
import { signIn } from "next-auth/react";
import { Form, Button, Alert } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import Facet from "@/components/Facet";
import { useState } from "react";

export default function LoginPage() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: { username: string; password: string }) => {
    setError("");
    setLoading(true);
    const res = await signIn("credentials", {
      redirect: false,
      username: values.username,
      password: values.password,
    });
    setLoading(false);
    if (res?.error) {
      setError("Invalid credentials. Please try again.");
      return;
    }
    // A hard navigation, not router.push: the session cookie NextAuth just
    // set needs a real request to be readable server-side, which is also
    // what /api/landing-page needs in order to answer correctly.
    let target = "/admin";
    try {
      const landing = await fetch("/api/landing-page");
      const data = await landing.json();
      if (data?.path) target = data.path;
    } catch {
      // Falls through to the default — a broken redirect lookup must not
      // strand someone who just entered valid credentials.
    }
    window.location.href = target;
  };

  return (
    <div className="login-wrap">
      <style>{`
        .login-wrap {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px 16px;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          background:
            radial-gradient(1100px 700px at 12% -10%, rgba(255, 214, 165, 0.55), transparent 60%),
            radial-gradient(900px 620px at 110% 8%, rgba(244, 63, 94, 0.40), transparent 58%),
            radial-gradient(760px 760px at 82% 120%, rgba(251, 191, 36, 0.42), transparent 60%),
            linear-gradient(150deg, #7C2D12 0%, #C2410C 42%, #EA580C 78%, #F97316 100%);
        }
        /* soft floating light blobs for depth */
        .login-wrap::before,
        .login-wrap::after {
          content: "";
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          z-index: -1;
          pointer-events: none;
        }
        .login-wrap::before {
          width: 360px; height: 360px;
          top: -120px; left: -100px;
          background: radial-gradient(circle at 30% 30%, rgba(255, 237, 213, 0.9), rgba(251, 146, 60, 0.15) 70%);
        }
        .login-wrap::after {
          width: 420px; height: 420px;
          right: -140px; bottom: -160px;
          background: radial-gradient(circle at 60% 40%, rgba(253, 164, 175, 0.75), rgba(234, 88, 12, 0) 72%);
        }
        /* faint dot texture layered above the blobs, below the card */
        .login-grain {
          position: absolute; inset: 0; z-index: -1; pointer-events: none;
          background-image: radial-gradient(rgba(255, 255, 255, 0.10) 1px, transparent 1px);
          background-size: 22px 22px;
          mask-image: radial-gradient(120% 120% at 50% 40%, #000 35%, transparent 100%);
        }
        /* white line-art sketches scattered behind the card */
        .login-art { position: absolute; inset: 0; z-index: -1; pointer-events: none; overflow: hidden; }
        .login-art svg {
          position: absolute;
          stroke: #fff; fill: none;
          stroke-width: 1.6;
          stroke-linecap: round;
          stroke-linejoin: round;
        }
        .art-fork    { top: 7%;  left: 6%;  width: 110px; opacity: .16; transform: rotate(-14deg); }
        .art-hat     { top: 10%; right: 7%; width: 130px; opacity: .15; transform: rotate(8deg); }
        .art-cup     { bottom: 9%; left: 8%; width: 120px; opacity: .15; transform: rotate(-6deg); }
        .art-plate   { bottom: 7%; right: 6%; width: 150px; opacity: .13; transform: rotate(10deg); }
        .art-wine    { top: 42%; left: 3%; width: 78px;  opacity: .12; transform: rotate(-4deg); }
        .art-pepper  { top: 38%; right: 4%; width: 66px;  opacity: .12; transform: rotate(12deg); }
        @media (max-width: 560px) {
          .art-wine, .art-pepper { display: none; }
          .art-fork, .art-hat, .art-cup, .art-plate { opacity: .1; }
        }
        .login-card {
          position: relative;
          width: 100%;
          max-width: 400px;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: saturate(1.4) blur(6px);
          border-radius: 20px;
          padding: 32px 22px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow:
            0 30px 70px -20px rgba(76, 15, 4, 0.5),
            0 10px 24px -12px rgba(76, 15, 4, 0.35),
            0 1px 0 rgba(255, 255, 255, 0.7) inset;
        }
        @media (min-width: 480px) {
          .login-card { padding: 40px 36px; }
        }
        .login-mark {
          width: 72px;
          height: 72px;
          margin: 0 auto 18px;
          border-radius: 18px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          box-shadow: 0 8px 22px -10px rgba(13, 27, 62, 0.45), 0 0 0 1px rgba(13, 27, 62, 0.06);
        }
        .login-mark img { width: 100%; height: 100%; object-fit: contain; }
        .login-card .ant-form-item { margin-bottom: 18px; }
      `}</style>

      <div className="login-grain" />

      <div className="login-art" aria-hidden="true">
        {/* fork + knife */}
        <svg className="art-fork" viewBox="0 0 48 60">
          <path d="M8 4v13a5 5 0 0 0 10 0V4M10 4v16M13 4v16M13 27v29" />
          <path d="M34 4c6 3 6 17 0 22l-2 1V4zM32 27v29" />
        </svg>
        {/* chef hat */}
        <svg className="art-hat" viewBox="0 0 64 60">
          <path d="M14 44h36v9a4 4 0 0 1-4 4H18a4 4 0 0 1-4-4z" />
          <path d="M17 44c-9 0-12-13-3-18-1-11 14-16 21-7 7-9 22-4 21 7 9 5 6 18-3 18" />
        </svg>
        {/* coffee cup */}
        <svg className="art-cup" viewBox="0 0 64 56">
          <path d="M8 16h34v18a12 12 0 0 1-12 12H20a12 12 0 0 1-12-12z" />
          <path d="M42 20h6a8 8 0 0 1 0 16h-6" />
          <path d="M18 4c0 4 4 4 4 8M28 4c0 4 4 4 4 8M38 4c0 4 4 4 4 8" />
          <path d="M6 52h40" />
        </svg>
        {/* place setting: plate + fork + knife */}
        <svg className="art-plate" viewBox="0 0 80 60">
          <circle cx="44" cy="30" r="20" />
          <circle cx="44" cy="30" r="13" />
          <path d="M8 6v10a4 4 0 0 0 8 0V6M12 6v13M12 22v32" />
          <path d="M72 6c4 2 4 13 0 17l-1 1V6zM71 24v30" />
        </svg>
        {/* wine glass */}
        <svg className="art-wine" viewBox="0 0 40 60">
          <path d="M8 4h24c0 18-6 24-12 24S8 22 8 4z" />
          <path d="M20 28v22M10 54h20" />
        </svg>
        {/* pepper shaker */}
        <svg className="art-pepper" viewBox="0 0 36 60">
          <path d="M8 22a10 10 0 0 1 20 0v28a4 4 0 0 1-4 4H12a4 4 0 0 1-4-4z" />
          <path d="M8 32h20" />
          <path d="M14 12v3M18 10v3M22 12v3M18 15v3" />
        </svg>
      </div>

      <div className="login-card">
        <div className="login-mark">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/icon.png" alt="ReMan" />
        </div>



        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 20, borderRadius: 8 }}
          />
        )}

        <Form layout="vertical" onFinish={handleSubmit} autoComplete="off" requiredMark={false}>
          <Facet.Text
            label="Username"
            name="username"
            required
            initialValue="a@b.com"
            placeholder="Enter username"
            prefix={<UserOutlined style={{ color: "#9ca3af" }} />}
          />
          <Facet.Text
            label="Password"
            name="password"
            initialValue="password123"
            required
            placeholder="Enter password"
            prefix={<LockOutlined style={{ color: "#9ca3af" }} />}
            type="password"
          />

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              size="large"
              style={{
                height: 46,
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                background: "linear-gradient(90deg, #EA580C, #F97316)",
                border: "none",
                boxShadow: "0 4px 14px rgba(234, 88, 12, 0.4)",
              }}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>

        <div
          style={{
            marginTop: 24,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            fontSize: 12,
            color: "#9ca3af",
          }}
        >
          <span>© {new Date().getFullYear()} ReMan · powered by</span>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/biralo_logo_128.png"
            alt="Biralosoft"
            style={{ height: 16, width: "auto", opacity: 0.8 }}
          />
          <span style={{ fontWeight: 600, color: "#6b7280" }}>Biralosoft</span>
        </div>
      </div>
    </div>
  );
}
